import { Dialog, Switch, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";
import { keys, without } from "lodash";
import React, { Fragment, useCallback } from "react";
import ChevronDownIcon from "../../../components/icons/ChevronDownIcon";
import ChevronUpIcon from "../../../components/icons/ChevronUpIcon";
import classNames from "../../../utils/classNames";
import { Entity } from "../../../utils/constants";
import useDataTable, { Property } from "./useDataTable";

interface Props<T> {
  entity: Entity;
  properties: { [key: string]: Property<T> };
  open: boolean;
  setOpen: (newOpen: boolean) => void;
}

function TablePropertiesSidebar<T>({
  entity,
  properties,
  open,
  setOpen
}: React.PropsWithChildren<Props<T>>) {
  const visibleProperties = useDataTable(
    useCallback((state) => state.visibleProperties[entity], [entity])
  );
  const updateVisibleProperties = useDataTable(
    (state) => state.updateVisibleProperties
  );
  const orderedProperties = useDataTable(
    useCallback((state) => state.orderedProperties[entity], [entity])
  );
  const updateOrderedProperties = useDataTable(
    (state) => state.updateOrderedProperties
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex sm:pl-16">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll dark:bg-gray-800">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                        Properties
                      </Dialog.Title>
                      <div className="ml-3 h-7 flex items-center">
                        <button
                          type="button"
                          className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                          onClick={() => setOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-300">
                      Show/hide and arrange properties
                    </p>
                  </div>
                  <ul className="flex-1 divide-y divide-gray-200 dark:divide-gray-500 overflow-y-auto border-t border-gray-200 dark:bg-gray-900 dark:border-gray-500">
                    {orderedProperties.map((key, index) => (
                      <Switch.Group
                        key={key}
                        as="div"
                        className="py-4 sm:py-5 flex items-center sm:pt-5 px-6"
                      >
                        <dd className="mt-1 flex items-center text-sm text-gray-900 dark:text-white sm:mt-0 mr-4">
                          <Switch
                            checked={visibleProperties.includes(key)}
                            onChange={() => {
                              const updatedVisibleProperties =
                                visibleProperties.includes(key)
                                  ? without(visibleProperties, key)
                                  : [...visibleProperties, key];
                              updateVisibleProperties(
                                entity,
                                updatedVisibleProperties
                              );
                            }}
                            className={classNames(
                              visibleProperties.includes(key)
                                ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                                : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400",
                              "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:ml-auto"
                            )}
                          >
                            <span className="sr-only">
                              Toggle {properties[key]?.title}
                            </span>
                            <span
                              aria-hidden="true"
                              className={classNames(
                                visibleProperties.includes(key)
                                  ? "translate-x-5"
                                  : "translate-x-0",
                                "inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200"
                              )}
                            />
                          </Switch>
                        </dd>
                        <Switch.Label
                          as="dt"
                          className="text-sm font-medium text-gray-500 flex-1 flex items-center dark:text-gray-200"
                          passive
                        >
                          <span className="flex-1">
                            {properties[key]?.title}
                          </span>

                          <span className="relative z-0 inline-flex shadow-sm rounded-md ml-4">
                            <button
                              type="button"
                              disabled={index === keys(properties).length - 1}
                              className="relative inline-flex items-center p-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-hotspotty-400 focus:border-hotspotty-400 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
                              onClick={() => {
                                const newOrderedProperties = [
                                  ...orderedProperties.slice(0, index),
                                  orderedProperties[index + 1],
                                  orderedProperties[index],
                                  ...orderedProperties.slice(index + 2)
                                ];

                                updateOrderedProperties(
                                  entity,
                                  newOrderedProperties
                                );
                              }}
                            >
                              <span className="sr-only">Move down</span>
                              <ChevronDownIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </button>
                            <button
                              type="button"
                              disabled={index === 0}
                              className="-ml-px relative inline-flex items-center p-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-hotspotty-400 focus:border-hotspotty-400 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
                              onClick={() => {
                                const newOrderedProperties = [
                                  ...orderedProperties.slice(0, index - 1),
                                  orderedProperties[index],
                                  orderedProperties[index - 1],
                                  ...orderedProperties.slice(index + 1)
                                ];

                                updateOrderedProperties(
                                  entity,
                                  newOrderedProperties
                                );
                              }}
                            >
                              <span className="sr-only">Move up</span>
                              <ChevronUpIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </button>
                          </span>
                        </Switch.Label>
                      </Switch.Group>
                    ))}
                  </ul>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default TablePropertiesSidebar;
